// src/utils/syncManager.js
//
// Drains the offline queue once connectivity is confirmed. Scores are
// drained first (small, safe to replay - CandidateScoreService upserts on
// a unique (assignment, criterion, student) constraint) and uploads second
// (larger, and re-posting an already-landed one duplicates document rows
// server-side until the backend's saveCandidateDocument is fixed to
// upsert, so each item's outcome is tracked individually rather than
// blanket-retried on any failure).
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import {
  getQueuedScores, removeQueuedScore,
  getQueuedUploads, updateQueuedUpload, removeQueuedUpload
} from './offlineQueue';

function isTokenExpired() {
  const token = localStorage.getItem('token');
  if (!token) return true;
  try {
    const { exp } = jwtDecode(token);
    return !exp || exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

async function drainScores() {
  const queued = await getQueuedScores();
  let synced = 0;
  const conflicts = [];
  for (const item of queued) {
    try {
      await axios.post(`/api/candidate-scores/assignment/${item.assignmentId}`, item.payload);
      await removeQueuedScore(item.key);
      synced += 1;
    } catch (err) {
      if (!err.response) {
        // Connectivity dropped again mid-drain - stop, leave the rest queued.
        return { synced, conflicts, stopped: true };
      }
      if (err.response.status === 401) {
        return { synced, conflicts, stopped: true, tokenExpired: true };
      }
      // A real rejection (already scored by someone else, candidate
      // absent, job completed) - not retryable, and the examiner needs to
      // see it rather than have it silently disappear.
      await removeQueuedScore(item.key);
      const message = typeof err.response.data === 'string'
        ? err.response.data
        : `Could not sync score for ${item.registrationNumber}.`;
      conflicts.push({ registrationNumber: item.registrationNumber, message });
    }
  }
  return { synced, conflicts, stopped: false };
}

function buildUploadFormData(item) {
  const formData = new FormData();
  item.files.forEach(({ blob, fileName }) => {
    formData.append('files', blob, fileName);
  });
  item.regNos.forEach((regNo) => formData.append('regNos', regNo));
  (item.alreadyUploaded || []).forEach((regNo) => formData.append('alreadyUploaded', regNo));
  return formData;
}

async function drainUploads() {
  const queued = (await getQueuedUploads()).filter((item) => item.status !== 'synced');
  let synced = 0;
  const failures = [];
  for (const item of queued) {
    try {
      await axios.post(`/api/assignments/${item.assignmentId}/bulk-candidate-work`, buildUploadFormData(item), {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await removeQueuedUpload(item.id);
      synced += 1;
    } catch (err) {
      if (!err.response) {
        return { synced, failures, stopped: true };
      }
      if (err.response.status === 401) {
        return { synced, failures, stopped: true, tokenExpired: true };
      }
      const message = typeof err.response.data === 'string'
        ? err.response.data
        : `Upload for assignment ${item.assignmentId} failed to sync.`;
      await updateQueuedUpload(item.id, { status: 'failed', lastError: message });
      failures.push({ assignmentId: item.assignmentId, regNos: item.regNos, message });
    }
  }
  return { synced, failures, stopped: false };
}

// `lockSession` triggers the existing AuthContext "locked" overlay so the
// user re-authenticates in place before anything replays - reused as-is
// rather than building new re-auth UI, since expired-token mid-session is
// already a handled case there.
export async function runSync({ lockSession } = {}) {
  if (isTokenExpired()) {
    lockSession?.();
    return null;
  }

  const scoreResult = await drainScores();
  if (scoreResult.tokenExpired) {
    lockSession?.();
  }

  let uploadResult = { synced: 0, failures: [], stopped: scoreResult.stopped };
  if (!scoreResult.stopped) {
    uploadResult = await drainUploads();
    if (uploadResult.tokenExpired) {
      lockSession?.();
    }
  }

  return {
    scoresSynced: scoreResult.synced,
    scoreConflicts: scoreResult.conflicts,
    uploadsSynced: uploadResult.synced,
    uploadFailures: uploadResult.failures
  };
}
